import { FC, useContext, useMemo, useRef, useState, useEffect } from "react";
import { ColumnConfig, WafflesPaginationProps } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import ProviderNetworkTypeBadge from "@app/shared/ProviderNetworkTypeBadge";
import { sortProviderNetworkTypes } from "@app/shared/enums/providerNetworkTypeEnum";
import ProviderActionStatusBadge from "@app/shared/ProviderActionStatusBadge";
import { CountyProjectContext } from "@app/projects/CountyProjectContext";
import ProviderHubService from "@app/projects/network-assessment/provider-hub/providerHubService";
import WaffleRowTooltip from "@app/shared/waffles/WaffleRowTooltip";
import { ProviderBadgeStyle } from "@app/projects/enumerators";
import PageSection from "@app/shared/PageSection";
import { QESButton } from "@app/shared/qes-standards/Button";
import { ProviderHubBasicFilters } from "@app/projects/network-assessment/provider-hub/ProvidersHubBasicFilters";
import { ProviderHubContext } from "@app/projects/network-assessment/provider-hub/contexts/ProviderHubContext";
import { formatGapImpactCount, formatSpecialtyWithCode, SortOrganizationByOptionToDisplay, NO_ACTIONS_APPLIED_LABEL } from "@app/projects/network-assessment/provider-hub/utility";
import { ProviderHubOrganizations, ProviderHubOrganizationSortColumn, ProviderHubOrganizationTableData } from "@app/projects/network-assessment/provider-hub/interfaces";
import { ProviderProfileReferrerEnum } from "@app/projects/network-assessment/provider-hub/provider-profile/interfaces/providerProfileReferrerEnum";
import { wafflesKeyColumnName } from "@app/shared/waffles/exports";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { ShowFullDataToggle } from "@app/projects/network-assessment/provider-hub/ShowFullDataToggle";
import { ProviderScoreColumnHeader } from "@app/projects/network-assessment/provider-hub/ProviderScoreColumnHeader";
import ProviderHubGenerateReportFlyout from "@app/projects/network-assessment/provider-hub/ProviderHubGenerateReportFlyout";

// Gap Impact "Sort by" options: the single combined pair vs. the four region-status pairs.
const combinedGapImpactSortOptions: ProviderHubOrganizationSortColumn[] = [
  ProviderHubOrganizationSortColumn.GapsClosedDescending,
  ProviderHubOrganizationSortColumn.GapsClosedAscending,
];

const breakdownGapImpactSortOptions: ProviderHubOrganizationSortColumn[] = [
  ProviderHubOrganizationSortColumn.ActiveGapsClosedDescending,
  ProviderHubOrganizationSortColumn.ActiveGapsClosedAscending,
  ProviderHubOrganizationSortColumn.PendingGapsClosedDescending,
  ProviderHubOrganizationSortColumn.PendingGapsClosedAscending,
  ProviderHubOrganizationSortColumn.ApprovedGapsClosedDescending,
  ProviderHubOrganizationSortColumn.ApprovedGapsClosedAscending,
  ProviderHubOrganizationSortColumn.OpportunityGapsClosedDescending,
  ProviderHubOrganizationSortColumn.OpportunityGapsClosedAscending,
];

const gapImpactSortColumns = new Set<ProviderHubOrganizationSortColumn>([
  ...combinedGapImpactSortOptions,
  ...breakdownGapImpactSortOptions,
]);

const ProviderHubOrganizationView: FC = () => {
  const projectContext = useContext(CountyProjectContext);
  const providerHubContext = useContext(ProviderHubContext);
  const providerHubService = useRef(new ProviderHubService()).current;

  const [sortChangedByDropdown, setSortChangedByDropdown] = useState(false);
  const [showFullOrganizationData, setShowFullOrganizationData] = useState(false);


  const showPci = providerHubContext.showPciFilters;
  const showMotive = projectContext.project.showMotiveAppropriatenessScore;
  const showCareJourney = projectContext.project.showCareJourneyScores;

  // PCI/Motive/CareJourney sort options only appear when the corresponding data is licensed and enabled.
  const pciSortOptions = new Set<ProviderHubOrganizationSortColumn>([
    ProviderHubOrganizationSortColumn.FilteredQualityDescending,
    ProviderHubOrganizationSortColumn.FilteredQualityAscending,
    ProviderHubOrganizationSortColumn.FilteredCostDescending,
    ProviderHubOrganizationSortColumn.FilteredCostAscending
  ]);
  const motiveSortOptions = new Set<ProviderHubOrganizationSortColumn>([
    ProviderHubOrganizationSortColumn.FilteredMotiveAppropriatenessDescending,
    ProviderHubOrganizationSortColumn.FilteredMotiveAppropriatenessAscending
  ]);
  const careJourneySortOptions = new Set<ProviderHubOrganizationSortColumn>([
    ProviderHubOrganizationSortColumn.FilteredCareJourneyCostDescending,
    ProviderHubOrganizationSortColumn.FilteredCareJourneyCostAscending,
    ProviderHubOrganizationSortColumn.FilteredCareJourneyQualityDescending,
    ProviderHubOrganizationSortColumn.FilteredCareJourneyQualityAscending
  ]);

  const isSortOptionVisible = (option: ProviderHubOrganizationSortColumn): boolean => {
    if (option === ProviderHubOrganizationSortColumn.Unknown) return false;
    if (pciSortOptions.has(option)) return showPci;
    if (motiveSortOptions.has(option)) return showMotive;
    if (careJourneySortOptions.has(option)) return showCareJourney;
    return true;
  };

  // Show the four-column region-status breakdown when the backend reports that this run carries region-status
  // data; otherwise fall back to the single Gap Impact column. These indicators are result-set-level metadata
  // (they cover every page), so paging never changes which columns or Gap Impact sort options are available.
  // The filtered and unfiltered ("Show Full Organization Data") columns are gated independently.
  const useRegionStatusBreakdown = providerHubContext.providerHubOrganizations?.useGapsByRegionStatus ?? false;
  const unfilteredUseRegionStatusBreakdown = providerHubContext.providerHubOrganizations?.unfilteredUseGapsByRegionStatus ?? false;

  const gapImpactSortOptions = useMemo<ProviderHubOrganizationSortColumn[]>(
    () => useRegionStatusBreakdown ? breakdownGapImpactSortOptions : combinedGapImpactSortOptions,
    [useRegionStatusBreakdown]
  );

  const otherSortOptions = useMemo<ProviderHubOrganizationSortColumn[]>(
    () => Object.values(ProviderHubOrganizationSortColumn).filter((option) => isSortOptionVisible(option) && !gapImpactSortColumns.has(option)),
    [showPci, showMotive, showCareJourney]
  );

  const sortByOptions = useMemo<ProviderHubOrganizationSortColumn[]>(
    () => [...gapImpactSortOptions, ...otherSortOptions],
    [gapImpactSortOptions, otherSortOptions]
  );

  /* oxlint-disable sort-keys */
  const columnConfig: ColumnConfig = {
    nameElement: {
      displayText: "Name",
      sizeContentToFit: true,
      sticky: true,
      testId: "provider-hub_organization-table-name",
      noSort: true
    },
    npi2: {
      displayText: "Identifier",
      sizeContentToFit: true,
      sticky: true,
      testId: "provider-hub_organization-table-npi2",
      noSort: true
    },
    providerSources: {
      displayText: "Provider Sources",
      sizeContentToFit: true,
      testId: "provider-hub_organization-table-provider-source",
      tooltip: "Indicates the original database from which the provider data was obtained",
      noSort: true
    },
    unfilteredProviderSources: {
      displayText: "Provider Sources - Unfiltered",
      sizeContentToFit: true,
      testId: "provider-hub_organization-table-unfiltered-provider-source",
      tooltip: "Indicates the original database from which the provider data was obtained",
      style: {backgroundColor: "#F4F9FF"},
      hidden: !showFullOrganizationData,
      noSort: true
    },
    statuses: {
      displayText: "Statuses",
      sizeContentToFit: true,
      testId: "provider-hub_organization-table-status",
      tooltip: "Indicates any actions that have been applied to providers within this organization",
      noSort: true
    },
    unfilteredStatuses: {
      displayText: "Statuses - Unfiltered",
      sizeContentToFit: true,
      testId: "provider-hub_organization-table-unfiltered-status",
      tooltip: "Indicates any actions that have been applied to providers within this organization",
      style: {backgroundColor: "#F4F9FF"},
      hidden: !showFullOrganizationData,
      noSort: true
    },
    gapsClosed: {
      displayText: "Gap Impact",
      sizeContentToFit: true,
      testId: "provider-hub_organization-table-gap-impact",
      tooltip: "Indicates the distinct number of gaps that the organization closes",
      noSort: true,
      hidden: useRegionStatusBreakdown
    },
    unfilteredGapsClosed: {
      displayText: "Gap Impact - Unfiltered",
      sizeContentToFit: true,
      testId: "provider-hub_organization-table-unfiltered-gap-impact",
      tooltip: "Indicates the distinct number of gaps that the organization closes",
      style: {backgroundColor: "#F4F9FF"},
      noSort: true,
      hidden: !showFullOrganizationData || unfilteredUseRegionStatusBreakdown
    },
    activeGapImpact: {
      displayText: "Active Gap Impact",
      sizeContentToFit: true,
      testId: "provider-hub_organization-table-active-gap-impact",
      tooltip: "Indicates the distinct number of gaps that the organization closes in active service area counties",
      noSort: true,
      hidden: !useRegionStatusBreakdown
    },
    unfilteredActiveGapImpact: {
      displayText: "Active Gap Impact - Unfiltered",
      sizeContentToFit: true,
      testId: "provider-hub_organization-table-unfiltered-active-gap-impact",
      tooltip: "Indicates the distinct number of gaps that the organization closes in active service area counties",
      style: {backgroundColor: "#F4F9FF"},
      noSort: true,
      hidden: !showFullOrganizationData || !unfilteredUseRegionStatusBreakdown
    },
    pendingGapImpact: {
      displayText: "Pending Gap Impact",
      sizeContentToFit: true,
      testId: "provider-hub_organization-table-pending-gap-impact",
      tooltip: "Indicates the distinct number of gaps that the organization closes in pending service area counties",
      noSort: true,
      hidden: !useRegionStatusBreakdown
    },
    unfilteredPendingGapImpact: {
      displayText: "Pending Gap Impact - Unfiltered",
      sizeContentToFit: true,
      testId: "provider-hub_organization-table-unfiltered-pending-gap-impact",
      tooltip: "Indicates the distinct number of gaps that the organization closes in pending service area counties",
      style: {backgroundColor: "#F4F9FF"},
      noSort: true,
      hidden: !showFullOrganizationData || !unfilteredUseRegionStatusBreakdown
    },
    approvedGapImpact: {
      displayText: "Approved Gap Impact",
      sizeContentToFit: true,
      testId: "provider-hub_organization-table-approved-gap-impact",
      tooltip: "Indicates the distinct number of gaps that the organization closes in approved service area counties",
      noSort: true,
      hidden: !useRegionStatusBreakdown
    },
    unfilteredApprovedGapImpact: {
      displayText: "Approved Gap Impact - Unfiltered",
      sizeContentToFit: true,
      testId: "provider-hub_organization-table-unfiltered-approved-gap-impact",
      tooltip: "Indicates the distinct number of gaps that the organization closes in approved service area counties",
      style: {backgroundColor: "#F4F9FF"},
      noSort: true,
      hidden: !showFullOrganizationData || !unfilteredUseRegionStatusBreakdown
    },
    opportunityGapImpact: {
      displayText: "Opportunity Gap Impact",
      sizeContentToFit: true,
      testId: "provider-hub_organization-table-opportunity-gap-impact",
      tooltip: "Indicates the distinct number of gaps that the organization closes in opportunity-to-expand service area counties",
      noSort: true,
      hidden: !useRegionStatusBreakdown
    },
    unfilteredOpportunityGapImpact: {
      displayText: "Opportunity Gap Impact - Unfiltered",
      sizeContentToFit: true,
      testId: "provider-hub_organization-table-unfiltered-opportunity-gap-impact",
      tooltip: "Indicates the distinct number of gaps that the organization closes in opportunity-to-expand service area counties",
      style: {backgroundColor: "#F4F9FF"},
      noSort: true,
      hidden: !showFullOrganizationData || !unfilteredUseRegionStatusBreakdown
    },
    servicingProviders: {
      displayText: "Servicing Providers",
      sizeContentToFit: true,
      testId: "provider-hub_organization-table-servicing-providers",
      noSort: true
    },
    unfilteredServicingProviders: {
      displayText: "Servicing Providers - Unfiltered",
      sizeContentToFit: true,
      testId: "provider-hub_organization-table-unfiltered-servicing-providers",
      style: {backgroundColor: "#F4F9FF"},
      noSort: true,
      hidden: !showFullOrganizationData
    },
    servicingSpecialties: {
      displayText: "Servicing Specialty Groups",
      sizeContentToFit: true,
      testId: "provider-hub_organization-table-servicing-specialties",
      tooltip: "Indicates the Specialty Groups that the Organization services",
      noSort: true
    },
    unfilteredServicingSpecialties: {
      displayText: "Servicing Specialty Groups - Unfiltered",
      sizeContentToFit: true,
      testId: "provider-hub_organization-table-unfiltered-servicing-specialties",
      tooltip: "Indicates the Specialty Groups that the Organization services",
      style: {backgroundColor: "#F4F9FF"},
      noSort: true,
      hidden: !showFullOrganizationData
    },
    outOfNetworkServicingProviders: {
      displayText: "Out-of-network Servicing Providers",
      sizeContentToFit: true,
      testId: "provider-hub_organization-table-oon-providers",
      noSort: true
    },
    unfilteredOutOfNetworkServicingProviders: {
      displayText: "Out-of-network Servicing Providers - Unfiltered",
      sizeContentToFit: true,
      testId: "provider-hub_organization-table-unfiltered-oon-providers",
      style: {backgroundColor: "#F4F9FF"},
      noSort: true,
      hidden: !showFullOrganizationData
    },
    inNetworkServicingProviders: {
      displayText: "In-network Servicing Providers",
      sizeContentToFit: true,
      testId: "provider-hub_organization-table-in-providers",
      noSort: true
    },
    unfilteredInNetworkServicingProviders: {
      displayText: "In-network Servicing Providers - Unfiltered",
      sizeContentToFit: true,
      testId: "provider-hub_organization-table-unfiltered-in-providers",
      style: {backgroundColor: "#F4F9FF"},
      noSort: true,
      hidden: !showFullOrganizationData
    },
    effectiveness: {
      hidden: projectContext.project.showPciData === false,
      displayText: "Effectiveness (Quality)",
      sizeContentToFit: true,
      testId: "provider-hub_organization-table-effectiveness",
      noSort: true
    },
    unfilteredEffectiveness: {
      displayText: "Effectiveness (Quality) - Unfiltered",
      sizeContentToFit: true,
      testId: "provider-hub_organization-table-unfiltered-effectiveness",
      style: {backgroundColor: "#F4F9FF"},
      noSort: true,
      hidden: !showFullOrganizationData || projectContext.project.showPciData === false
    },
    efficiency: {
      hidden: projectContext.project.showPciData === false,
      displayText: "Efficiency (Cost)",
      sizeContentToFit: true,
      testId: "provider-hub_organization-table-efficiency",
      noSort: true
    },
    unfilteredEfficiency: {
      displayText: "Efficiency (Cost) - Unfiltered",
      sizeContentToFit: true,
      testId: "provider-hub_organization-table-unfiltererd-efficiency",
      style: {backgroundColor: "#F4F9FF"},
      noSort: true,
      hidden: !showFullOrganizationData || projectContext.project.showPciData === false
    },
    motiveAppropriateness: {
      displayElement: <ProviderScoreColumnHeader variant="motive" />,
      sizeContentToFit: true,
      testId: "provider-hub_organization-table-motive-appropriateness",
      noSort: true,
      hidden: !projectContext.project.showMotiveAppropriatenessScore
    },
    unfilteredMotiveAppropriateness: {
      displayElement: <ProviderScoreColumnHeader variant="motive" label="Motive Appropriateness - Unfiltered" />,
      sizeContentToFit: true,
      testId: "provider-hub_organization-table-unfiltered-motive-appropriateness",
      style: {backgroundColor: "#F4F9FF"},
      noSort: true,
      hidden: !showFullOrganizationData || !projectContext.project.showMotiveAppropriatenessScore
    },
    careJourneyCost: {
      displayElement: <ProviderScoreColumnHeader variant="careJourneyCost" />,
      sizeContentToFit: true,
      testId: "provider-hub_organization-table-carejourney-cost",
      noSort: true,
      hidden: !projectContext.project.showCareJourneyScores
    },
    unfilteredCareJourneyCost: {
      displayElement: <ProviderScoreColumnHeader variant="careJourneyCost" label="CareJourney Cost - Unfiltered" />,
      sizeContentToFit: true,
      testId: "provider-hub_organization-table-unfiltered-carejourney-cost",
      style: {backgroundColor: "#F4F9FF"},
      noSort: true,
      hidden: !showFullOrganizationData || !projectContext.project.showCareJourneyScores
    },
    careJourneyQuality: {
      displayElement: <ProviderScoreColumnHeader variant="careJourneyQuality" />,
      sizeContentToFit: true,
      testId: "provider-hub_organization-table-carejourney-quality",
      noSort: true,
      hidden: !projectContext.project.showCareJourneyScores
    },
    unfilteredCareJourneyQuality: {
      displayElement: <ProviderScoreColumnHeader variant="careJourneyQuality" label="CareJourney Quality - Unfiltered" />,
      sizeContentToFit: true,
      testId: "provider-hub_organization-table-unfiltered-carejourney-quality",
      style: {backgroundColor: "#F4F9FF"},
      noSort: true,
      hidden: !showFullOrganizationData || !projectContext.project.showCareJourneyScores
    }
  };
  /* oxlint-enable sort-keys */

  const paginationProps: WafflesPaginationProps = {
    changePage: (pageNumber: number) => handlePageChange(pageNumber),
    enableBackendPagination: true,
    handlePageSizeChange: (pageSize: number) => handlePageSizeChange(pageSize),
    initialPage: providerHubContext.pageIndex,
    pageSizeOptions: [50, 100, 150],
    recordsPerPage: providerHubContext.pageSize,
    resetToFirstPage: providerHubContext.resetToFirstPage,
    showPageSizeSelector: true,
    totalRecords: providerHubContext.providerHubOrganizations?.totalCount ?? 0
  };

  const handlePageSizeChange = (pageSize: number) => {
    providerHubContext.setPageSize(pageSize);
    providerHubContext.setPageIndex(1);
  };

  const handlePageChange = (newPage: number) => {
    if (!providerHubContext.resetToFirstPage) {
      providerHubContext.setPageIndex(newPage);
    }
    if (providerHubContext.resetToFirstPage) {
      providerHubContext.setResetToFirstPage(false);
    }
  };

  const handleOrganizationClick = async (org: ProviderHubOrganizations) => {
    const [orgDetails, contractEntityDetails] = await Promise.all([
      providerHubService.getOrganizationDetails(
        projectContext?.project?.projectId,
        providerHubContext.filterState.projectRunConfigurationId,
        org.npi,
        org.contractEntityId
      ),
      providerHubService.getContractEntityDetails(
        projectContext?.project?.projectId,
        providerHubContext.filterState.projectRunConfigurationId,
        org.contractEntityId
      ),
    ]);
    providerHubContext.navigateToOrganizationProfile(orgDetails, contractEntityDetails, ProviderProfileReferrerEnum.ProviderHubLandingPage);
  };

  const [isGenerateReportModalOpen, setIsGenerateReportModalOpen] = useState(false);

  /* oxlint-disable sort-keys */
  const organizationTableData = useMemo<ProviderHubOrganizationTableData[]>(() => {
    return (providerHubContext.providerHubOrganizations?.items ?? []).map((org) => ({
      [wafflesKeyColumnName]: org.contractEntityId.toString(),
      nameElement: (
        <QESButton
          qesType="link"
          qesTestId={`provider-hub-organization-view__org--${org.name}`}
          onClick={() => handleOrganizationClick(org)}
          className="demibold"
        >
          {org.name ?? "---"}
        </QESButton>
      ),
      name: org.name,
      npi2: `NPI (Type 2): ${org.npi}`,
      providerSources: (
        <>
          {sortProviderNetworkTypes(org.providerNetworkTypes ?? []).map((networkType) =>
            <ProviderNetworkTypeBadge badgeStyle={ProviderBadgeStyle.Outline} key={networkType} providerNetworkType={networkType} />
          )}
        </>
      ),
      unfilteredProviderSources: (
        <>
          {sortProviderNetworkTypes(org.unfilteredProviderNetworkTypes ?? []).map((networkType) =>
            <ProviderNetworkTypeBadge badgeStyle={ProviderBadgeStyle.Outline} key={networkType} providerNetworkType={networkType} />
          )}
        </>
      ),
      statuses: (org.providerActionStatuses ?? []).length > 0 ? (
        <>
          {org.providerActionStatuses.map((status, index) =>
            <ProviderActionStatusBadge badgeStyle={ProviderBadgeStyle.Dot} key={index} status={status} />
          )}
        </>
      ) : (
        <>{NO_ACTIONS_APPLIED_LABEL}</>
      ),
      unfilteredStatuses: (org.unfilteredProviderActionStatuses ?? []).length > 0 ? (
        <>
          {org.unfilteredProviderActionStatuses.map((status, index) =>
            <ProviderActionStatusBadge badgeStyle={ProviderBadgeStyle.Dot} key={index} status={status} />
          )}
        </>
      ) : (
        <>{NO_ACTIONS_APPLIED_LABEL}</>
      ),
      gapsClosed: formatGapImpactCount(org.gapsClosed),
      unfilteredGapsClosed: formatGapImpactCount(org.unfilteredGapsClosed),
      activeGapImpact: formatGapImpactCount(org.activeGapsClosed),
      unfilteredActiveGapImpact: formatGapImpactCount(org.unfilteredActiveGapsClosed),
      pendingGapImpact: formatGapImpactCount(org.pendingGapsClosed),
      unfilteredPendingGapImpact: formatGapImpactCount(org.unfilteredPendingGapsClosed),
      approvedGapImpact: formatGapImpactCount(org.approvedGapsClosed),
      unfilteredApprovedGapImpact: formatGapImpactCount(org.unfilteredApprovedGapsClosed),
      opportunityGapImpact: formatGapImpactCount(org.opportunityGapsClosed),
      unfilteredOpportunityGapImpact: formatGapImpactCount(org.unfilteredOpportunityGapsClosed),
      servicingProviders: org.servicingProviders,
      unfilteredServicingProviders: org.unfilteredServicingProviders,
      servicingSpecialties: (
        <WaffleRowTooltip
          containerClassName="provider-hub-waffle-tooltip"
          elements={org.filteredServicingSpecialties?.map(formatSpecialtyWithCode) ?? []}
          numberOfElementsPerRow={3}
        />
      ),
      unfilteredServicingSpecialties: (
        <WaffleRowTooltip
          containerClassName="provider-hub-waffle-tooltip"
          elements={org.unfilteredServicingSpecialties?.map(formatSpecialtyWithCode) ?? []}
          numberOfElementsPerRow={3}
        />
      ),
      outOfNetworkServicingProviders: org.outOfNetworkServicingProviders,
      unfilteredOutOfNetworkServicingProviders: org.unfilteredOutOfNetworkServicingProviders,
      inNetworkServicingProviders: org.inNetworkServicingProviders,
      unfilteredInNetworkServicingProviders: org.unfilteredInNetworkServicingProviders,
      effectiveness: org.quality ?? "---",
      unfilteredEffectiveness: org.unfilteredQuality ?? "---",
      efficiency: org.cost ?? "---",
      unfilteredEfficiency: org.unfilteredCost ?? "---",
      motiveAppropriateness: org.motiveAppropriateness ?? "---",
      unfilteredMotiveAppropriateness: org.unfilteredMotiveAppropriateness ?? "---",
      careJourneyCost: org.careJourneyCost ?? "---",
      unfilteredCareJourneyCost: org.unfilteredCareJourneyCost ?? "---",
      careJourneyQuality: org.careJourneyQuality ?? "---",
      unfilteredCareJourneyQuality: org.unfilteredCareJourneyQuality ?? "---",
    }));
  }, [providerHubContext.providerHubOrganizations]);
  /* oxlint-enable sort-keys */

  const handleSortChange = (newSortValue: ProviderHubOrganizationSortColumn) => {
    providerHubContext.setFilter('sortOrganizationColumnAndOrder', newSortValue)
    setSortChangedByDropdown(true);
  };

  useEffect(() => {
    if (sortChangedByDropdown) {
      providerHubContext.handleSearchClick();
    }
    setSortChangedByDropdown(false);
  }, [providerHubContext.filterState.sortOrganizationColumnAndOrder, sortChangedByDropdown]);

  // When the display mode flips (combined <-> breakdown) the previously selected Gap Impact sort may
  // no longer appear in the dropdown; reset to the default and re-search so results reflect the reset.
  useEffect(() => {
    const currentSort = providerHubContext.filterState.sortOrganizationColumnAndOrder;
    if (!sortByOptions.includes(currentSort)) {
      providerHubContext.setFilter('sortOrganizationColumnAndOrder', ProviderHubOrganizationSortColumn.NameAscending);
      setSortChangedByDropdown(true);
    }
  }, [sortByOptions]);

  return (
    <PageSection>
      <>
        <ProviderHubBasicFilters />
        <div className="provider-hub-providers-table-info-header">
          <div>
            <h5>Organizations</h5>
            <p>View and select an organization listed below</p>
            <QESSelect
            allowEmpty={false}
            className="sort-by-option"
            testId="provider-hub__sort-by-select"
            label="Sort by"
            onChange={(e) => handleSortChange(e.target.value as ProviderHubOrganizationSortColumn)}
            value={providerHubContext.filterState.sortOrganizationColumnAndOrder}
          >
            <optgroup label="Gap Impact">
              {gapImpactSortOptions.map((option) => (
                <option key={option} value={option}>
                  {SortOrganizationByOptionToDisplay(option)}
                </option>
              ))}
            </optgroup>
            <optgroup label="Other">
              {otherSortOptions.map((option) => (
                <option key={option} value={option}>
                  {SortOrganizationByOptionToDisplay(option)}
                </option>
              ))}
            </optgroup>
          </QESSelect>
          </div>
          <div className="provider-hub-organization-view__reporting-container">
            {organizationTableData.length > 0 && (
              <QESButton
                qesType="primary"
                qesTestId="provider-hub-organization-view__generate-report"
                className="mb-2"
                onClick={() => setIsGenerateReportModalOpen(true)}
              >
                Generate Report
              </QESButton>
            )}
          </div>
        </div>
        <ProviderHubGenerateReportFlyout
          isModalOpen={isGenerateReportModalOpen}
          setIsModalOpen={setIsGenerateReportModalOpen}
          filters={providerHubContext.filterState}
        />
        <div className="provider-hub-organization-view__record-count">
          { providerHubContext.showFilterToggle && (
            <ShowFullDataToggle
              label={"Show Full Organization Data"}
              tooltipText={"Organizations display both the filtered data, based on search criteria, and unfiltered data"}
              active={showFullOrganizationData}
              onChange={() => {setShowFullOrganizationData(!showFullOrganizationData)}}
              testId={"provider-hub-organization-view_full-org-toggle"}
            />
          )}
          <p>Organizations: {providerHubContext.providerHubOrganizations?.totalCount?.toLocaleString() ?? 0}</p>
        </div>
        <Waffles<ProviderHubOrganizationTableData>
          columns={columnConfig}
          data={organizationTableData}
          totalNumberOfRecords={organizationTableData.length}
          enablePagination={true}
          emptyTableMessage="No matching organization results found. Please start a new search"
          fullWidth={true}
          loading={providerHubContext.loading}
          paginationProps={paginationProps}
          testId="provider-hub__organization-table"
          variant="frozen-border"
        />
      </>
    </PageSection>
  );
};

export default ProviderHubOrganizationView;
